import React, { useState } from 'react';
import { Button } from './ui/button';
import { 
  Share2, 
  Copy, 
  MessageCircle, 
  Check,
  X,
  Shield,
  Link2,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ShareTripButton({ tripId, tripType = 'ride', className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const getShareLink = async () => {
    setLoading(true);
    const token = localStorage.getItem('mercapoli_token');
    
    try {
      const response = await axios.post(
        `${API_BASE}/api/trips/${tripId}/share`,
        { trip_type: tripType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShareLink(response.data);
      setIsOpen(true);
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al generar enlace';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!shareLink?.share_url) return;
    
    try {
      await navigator.clipboard.writeText(shareLink.share_url);
      setCopied(true);
      toast.success('Enlace copiado');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Error al copiar');
    }
  };

  const shareViaWhatsApp = () => {
    if (!shareLink?.share_url) return;
    
    const message = encodeURIComponent(
      `🚗 ¡Sigue mi viaje en tiempo real!\n\n` +
      `Mi viaje está siendo monitoreado por la Central de Seguridad Mercapoli Move 24/7.\n\n` +
      `👉 ${shareLink.share_url}`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const revokeLink = async () => {
    const token = localStorage.getItem('mercapoli_token');
    
    try {
      await axios.delete(
        `${API_BASE}/api/trips/${tripId}/share`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShareLink(null);
      setIsOpen(false);
      toast.success('Enlace revocado');
    } catch {
      toast.error('Error al revocar enlace');
    }
  };

  if (!isOpen) {
    return (
      <Button
        onClick={getShareLink}
        disabled={loading}
        variant="outline"
        className={`border-teal-200 text-teal-700 hover:bg-teal-50 ${className}`}
        data-testid="share-trip-button"
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-teal-300 border-t-teal-600 rounded-full animate-spin mr-2" />
        ) : (
          <Share2 className="w-4 h-4 mr-2" />
        )}
        Compartir mi viaje
      </Button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl animate-in slide-in-from-bottom">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
              <Shield className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800">Compartir mi viaje</h3>
              <p className="text-xs text-slate-500">Monitoreado por Central Mercapoli</p>
            </div>
          </div>
          <button 
            onClick={() => setIsOpen(false)}
            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Security Message */}
          <div className="p-3 bg-teal-50 border border-teal-100 rounded-xl">
            <p className="text-sm text-teal-700 text-center">
              <Shield className="w-4 h-4 inline mr-1" />
              Quien reciba este enlace podrá ver tu ubicación y el estado del viaje en tiempo real
            </p>
          </div>

          {/* Link Preview */}
          {shareLink && (
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <Link2 className="w-4 h-4 text-slate-400" />
                <span className="text-xs text-slate-500 font-medium">Enlace de seguimiento</span>
              </div>
              <p className="text-sm text-slate-700 truncate font-mono bg-white px-2 py-1 rounded">
                {shareLink.share_url}
              </p>
              {shareLink.views_count > 0 && (
                <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
                  <Users className="w-3 h-3" />
                  <span>{shareLink.views_count} visualizaciones</span>
                </div>
              )}
            </div>
          )}

          {/* Share Options */}
          <div className="space-y-2">
            <Button
              onClick={copyToClipboard}
              variant="outline"
              className="w-full justify-start h-12 border-slate-200 hover:bg-slate-50"
              data-testid="copy-link-button"
            >
              {copied ? (
                <Check className="w-5 h-5 text-green-500 mr-3" />
              ) : (
                <Copy className="w-5 h-5 text-slate-400 mr-3" />
              )}
              <span className="text-slate-700">{copied ? 'Copiado!' : 'Copiar enlace'}</span>
            </Button>

            <Button
              onClick={shareViaWhatsApp}
              className="w-full justify-start h-12 bg-green-500 hover:bg-green-600 text-white"
              data-testid="share-whatsapp-button"
            >
              <MessageCircle className="w-5 h-5 mr-3" />
              Enviar por WhatsApp
            </Button>
          </div>

          {/* Info */}
          <div className="text-center text-xs text-slate-400 pt-2">
            El enlace expira 30 minutos después de finalizar el viaje
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100">
          <Button
            onClick={revokeLink}
            variant="ghost"
            className="w-full text-red-500 hover:text-red-600 hover:bg-red-50"
            data-testid="revoke-link-button"
          >
            Revocar enlace
          </Button>
        </div>
      </div>
    </div>
  );
}
